import { getTargetPeriod, formatPeriod } from '../../utils/periodUtils';
import {
  shouldShowApproveRejectButtons,
  shouldShowRevisionButton,
  isDocumentViewOnly,
} from '../../utils/documentButtonVisibility';
export interface DocumentDetail {
  id: string;
  file_location: string;
  upload_date: string;
  expiry_date?: string | null;
  applicable_month?: string | null;
  applicable_year?: number | null;
  status: string;
  approval_timestamp: string | null;
  approved_by_user_id: string | null;
  rejection_reason: string | null;
  original_filename?: string | null;
  udr_is_current?: boolean | null;
  udr_due_date?: string | null;
  udr_status?: string;
  is_deleted?: boolean;
  documentName?: string; // Added for modal display
  requirement_id: string;
  requirement: {
    id: string;
    status: string;
    due_date: string | null;
    definition: {
      name: string;
      description: string | null;
      renewal_type: string | null;
      renewal_interval: any;
      category: {
        name: string;
      } | null;
    };
  };
}

export interface ReviewRequirement {
  id: string;
  definition_id: string;
  due_date: string | null;
  status: string;
  udr_is_current?: boolean | null;
  definition: DocumentDetail['requirement']['definition'] & {
    id: string;
    is_required?: boolean;
    is_active?: boolean;
  };
  documents: DocumentDetail[];
}
export function validMonth(value: string | null | undefined): value is string {
  return !!value && /^(19|20|21)\d{2}-(0[1-9]|1[0-2])$/.test(value);
}
export function periodLabel(value: string | null) {
  if (!validMonth(value)) return 'Periodo no disponible';
  const [year, month] = value.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('es-MX', {
    month: 'long',
    year: 'numeric',
  });
}
export function filePeriod(doc: DocumentDetail) {
  const period = getTargetPeriod({
    applicableMonth: doc.applicable_month,
    applicableYear: doc.applicable_year,
    dueDate: doc.requirement?.due_date,
    uploadDate: doc.upload_date,
    renewalType: doc.requirement?.definition?.renewal_type,
    renewalInterval: doc.requirement?.definition?.renewal_interval,
  });
  return formatPeriod(period.month, period.year);
}
// Display only explicit applicability. Legacy inference remains available for navigation.
export function filePeriodLabel(doc: DocumentDetail) {
  return doc.applicable_month && doc.applicable_year
    ? periodLabel(filePeriod(doc))
    : 'No especificado';
}
export function versions(requirement: ReviewRequirement) {
  return requirement.documents
    .filter((doc) => !doc.is_deleted)
    .map((doc) => ({
      ...doc,
      documentName: requirement.definition.name,
      requirement: {
        id: doc.requirement_id,
        status: doc.udr_status ?? requirement.status,
        due_date: doc.udr_due_date !== undefined ? doc.udr_due_date : requirement.due_date,
        definition: requirement.definition,
      },
    }))
    .sort(
      (a, b) =>
        Date.parse(b.upload_date) - Date.parse(a.upload_date) ||
        a.id.localeCompare(b.id),
    );
}
export function allowedActions(
  doc: DocumentDetail,
  role: string | undefined,
  siblings: DocumentDetail[],
) {
  const privileged = role === 'admin' || role === 'reviewer';
  const state = { ...doc, udr_is_current: doc.udr_is_current ?? null };
  const valid = privileged && !!doc.requirement_id && !doc.is_deleted;
  return {
    approve: valid && shouldShowApproveRejectButtons(state),
    reject: valid && shouldShowApproveRejectButtons(state),
    revision: valid && shouldShowRevisionButton(state),
    delete:
      valid &&
      role === 'admin' &&
      doc.status === 'rejected' &&
      !isDocumentViewOnly(state),
    discard:
      valid &&
      doc.status === 'pending_approval' &&
      siblings.some(
        (other) =>
          other.id !== doc.id &&
          !other.is_deleted &&
          other.status === 'approved',
      ),
  };
}
export function optionalApproval(
  requirement: ReviewRequirement,
  role?: string,
) {
  return (
    (role === 'admin' || role === 'reviewer') &&
    requirement.udr_is_current !== false &&
    requirement.definition.is_required === false &&
    !versions(requirement).some(
      (doc) =>
        doc.status === 'approved' ||
        (doc.status === 'pending_approval' && !doc.rejection_reason),
    )
  );
}
export function fileLabel(doc: DocumentDetail) {
  return (
    doc.original_filename ||
    doc.file_location?.split('/').pop() ||
    'Archivo sin nombre'
  );
}
export function timestamp(value: string | null) {
  if (!value) return 'No disponible';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'No disponible'
    : date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}
export function detailStatus(doc: DocumentDetail) {
  if (
    doc.status === 'pending_approval' &&
    doc.rejection_reason?.startsWith('SOLICITUD DE REVISIÓN:')
  )
    return 'Revisión solicitada';
  return (
    (
      {
        pending_approval: 'Pendiente de aprobación',
        approved: 'Aprobado',
        rejected: 'Rechazado',
        reemplazado: 'Reemplazado',
        superseded: 'Sustituido',
        expired: 'Vencido',
        pending_upload: 'Pendiente de carga',
      } as Record<string, string>
    )[doc.status] || 'Estado no disponible'
  );
}
